export interface SpinnerProps {
  /** Diameter tier. `sm` ≈ 14px (inline with text/buttons), `md` ≈ 20px. */
  size?: "sm" | "md";
  /** Accessible label announced to screen readers. Defaults to "Loading". */
  label?: string;
  className?: string;
}

/**
 * Spinner — an indeterminate activity indicator for in-flight async work
 * (Compare dry-run, Export run) where no progress fraction is known yet.
 *
 * A ring that inherits the current text color (`border-current` with a
 * transparent top arc) and spins via Tailwind's `animate-spin`. Color is
 * inherited so callers tint it through `text-*` tokens (e.g. wrap in
 * `text-accent`) — keeps the primitive token-agnostic and reusable.
 *
 * Rendered as a native `<output>` (implicit `role="status"`) so assistive tech
 * announces it; the ring itself is `aria-hidden` decoration.
 */
export function Spinner({ size = "sm", label = "Loading", className }: SpinnerProps) {
  const dims = size === "md" ? "h-5 w-5 border-2" : "h-3.5 w-3.5 border-2";
  const classes = [
    "inline-block animate-spin rounded-full border-current border-t-transparent align-[-0.125em]",
    dims,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <output aria-label={label} className="inline-flex">
      <span aria-hidden="true" className={classes} />
    </output>
  );
}
